import { clamp, cx, formatMs } from "./utils";
import { Icon } from "./Icon";

export type RecordState = "idle" | "recording" | "disabled";

export interface RecordButtonProps {
  /** Current state. `disabled` blocks interaction (e.g. consent unsigned). */
  state: RecordState;
  /** Toggle handler: start when idle, stop when recording. */
  onToggle: () => void;
  /** Elapsed recording time in ms (shown under the icon). */
  elapsedMs?: number;
  /** Hard limit in ms — drives the surrounding progress ring (§9: 22 000). */
  maxMs?: number;
  /** Diameter in px; floored at 72. @default 96 */
  size?: number;
  /** Override the accessible label. */
  label?: string;
  className?: string;
}

/**
 * Large circular record control (≥72px) with an elapsed readout and a ring
 * showing progress toward `maxMs`. Space/Enter activate it as a native button.
 */
export function RecordButton({
  state,
  onToggle,
  elapsedMs = 0,
  maxMs,
  size = 96,
  label,
  className,
}: RecordButtonProps) {
  const d = Math.max(72, size);
  const recording = state === "recording";
  const disabled = state === "disabled";
  const progress = maxMs && maxMs > 0 ? clamp(elapsedMs / maxMs, 0, 1) : 0;
  const thickness = 5;
  const r = (d - thickness) / 2;
  const c = 2 * Math.PI * r;
  const name = label ?? (recording ? "停止录音" : "开始录音");

  return (
    <div
      className={cx("xz-record", recording && "is-recording", disabled && "is-disabled", className)}
    >
      <svg
        className="xz-record__ring"
        width={d}
        height={d}
        viewBox={`0 0 ${d} ${d}`}
        aria-hidden="true"
      >
        <circle
          className="xz-record__ring-track"
          cx={d / 2}
          cy={d / 2}
          r={r}
          strokeWidth={thickness}
          fill="none"
        />
        {maxMs != null && (
          <circle
            className="xz-record__ring-bar"
            cx={d / 2}
            cy={d / 2}
            r={r}
            strokeWidth={thickness}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - progress)}
            transform={`rotate(-90 ${d / 2} ${d / 2})`}
          />
        )}
      </svg>
      <button
        type="button"
        className="xz-record__btn"
        style={{ width: d - thickness * 2 - 6, height: d - thickness * 2 - 6 }}
        aria-label={name}
        aria-pressed={recording}
        disabled={disabled}
        onClick={onToggle}
      >
        <Icon id={recording ? "ui-stop" : "ui-microphone"} size={24} />
      </button>
      <span className="xz-record__time" aria-live={recording ? "off" : undefined}>
        {formatMs(elapsedMs, true)}
        {maxMs != null && <span className="xz-record__max"> / {formatMs(maxMs)}</span>}
      </span>
    </div>
  );
}
